import React, { useState, useEffect } from 'react'
import { detect } from 'detect-browser'
import styled from 'styled-components'
import moment from 'moment'
import Paging from '../pagination/Paging'

// components
import DetailItem from './DetailItem'
import DropDown from '../../components/dropdown/DropDown'
import Empty from '../../pages/reviewList/components/Empty'

// images
import IconDown from '../../assets/image/arrow/ic-down.svg'
import IconUp from '../../assets/image/arrow/ic-up.svg'

// utils
import { useAxios } from '../../utils/useAxios'
import { getComma } from '../../utils/getComma'
import { getOrderDetail } from '../../utils/getOrderDetail'

// globals
import * as constants from '../../globals/constants'
import * as TYPE from '../../globals/type'

// [주문현황 테이블]
const OrderDetail = ({ orderMode, send, setData, agentPOSData }) => {
  const user = JSON.parse(localStorage.getItem('user'))
  const browser = detect()
  const [isAlert, setIsAlert] = useState(false)
  const [orderData, setOrderData] = useState([])
  const [pageCount, setPageCount] = useState(0) // 페이지 next 체크

  const { fetchData, loading, data, error } = useAxios(isAlert, setIsAlert)

  // 페이지네이션
  const [count, setCount] = useState(0) //아이템 총 개수
  const [currentpage, setCurrentpage] = useState(1) // 현재페이지
  const [currentsize, setCurrentsize] = useState(10) //페이지당 아이템 개수 (10)
  const sizeOption = ['10', '30', '50', '100']

  const nextPage = (page) => {
    setCurrentpage(page)
  }

  // 아코디언 요소
  const [expandedItems, setExpandedItems] = useState([]) //개별
  const [allExpanded, setAllExpanded] = useState(false) //모든

  // 개인정보 숨겨야 하는 주문(접수번호)
  const [hiddenOrder, setHiddenOrder] = useState([])

  // 개별 펼쳐보기(상세)
  const toggleExpand = (itemId) => {
    setExpandedItems((prevExpanded) => {
      if (prevExpanded.includes(itemId)) {
        return prevExpanded.filter((id) => id !== itemId)
      } else {
        return [...prevExpanded, itemId]
      }
    })
  }

  // 모두 펼쳐보기(상세)
  const toggleExpandAll = () => {
    if (allExpanded) {
      setExpandedItems([])
    } else {
      // 모든 아이템 열기
      const itemsToExpand = orderData.map((item) => item.orderNum)
      setExpandedItems(itemsToExpand)
    }
    setAllExpanded(!allExpanded)
  }

  useEffect(() => {
    if (agentPOSData) {
      setOrderData(agentPOSData.responseData?.orderList)
      setData(agentPOSData)
      setCount(agentPOSData.responseData?.itemCount)
    }
  }, [agentPOSData])

  // 주문현황 조회
  const getItems = (size, page, send, orderMode) => {
    const params = {
      size: size,
      page: page,
      orderType: send?.orderType,
      detailType: send?.orderDetail,
      orderStatus: send?.orderState === '308' ? undefined : send?.orderState,
      selfDelivery: send?.orderState === '308' ? 'Y' : undefined,
      deliveryStatus: send?.orderDelivery,
      orderNum: send?.orderNum,
      orderMode: orderMode,
      startDate: send?.startDate,
      endDate: send?.endDate,
      ...(orderMode === 'TODAY' && {
        startDate: moment().format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
      }),
    }

    fetchData(
      constants.API.ORDERS_LIST,
      'GET', // method
      null, // body
      params, // params
    )
  }

  // 주소 정보 열람 히스토리 추가
  const setOrderHistoryItem = (order) => {
    const body = {
      appId: TYPE.ID.WEB, // 아이디(에이전트, 사장님웹 구분 값)
      osName: browser.os ?? '', // 운영체제
      pageName:
        orderMode === 'TODAY' // 페이지명
          ? 'todayOrderListPage.TodayOrderList'
          : 'selectOrderListPage.SelectOrderList',
      typeCode: TYPE.ACTION.READ, // 열람
      orderId: order.orderNum, // 주문번호
      orderMessage: `로그인아이디[${user?.userId}]가 오더번호[${order.orderNum}]의 상세주소 열람`, // 저장 메시지
      receiptId: order.ReceiptId,
    }

    // 버튼 클릭하면 해당 접수번호 추가
    setHiddenOrder([...hiddenOrder, order.ReceiptId])

    fetchData(constants.API.WORK_HISTORY_ORDER, 'POST', body)
  }

  const initState = () => {
    setData()
    setCount(0)
    setOrderData([])
  }

  useEffect(() => {
    let resCode = data?.responseCode
    let resData = data?.responseData

    switch (resCode) {
      // 성공(데이터 O)
      case TYPE.API_RESPONSE.SUCCESS:
        if (resData) {
          // 로그
          if (resData.hasOwnProperty('selfDelivery')) {
            if (resData?.isMasking === 'N') {
              setHiddenOrder((prevExpanded) => {
                if (prevExpanded.includes(Number(resData.id))) {
                  return prevExpanded.filter((id) => id !== Number(resData.id))
                }
              })
            }
          }
          // 목록
          else {
            // 요약
            setData(resData)
            // 페이지네이션
            setCount(resData.itemCount)
            // 목록
            setOrderData(resData.orderList)
          }
          break
        }
      // 성공(데이터X)
      case TYPE.API_RESPONSE.NOT_DATA:
        initState()
        break

      // 실패
      case TYPE.API_RESPONSE.FAILURE:
      default:
        break
    }
  }, [data])

  useEffect(() => {
    if (pageCount > 0) {
      getItems(currentsize, currentpage, send, orderMode)
      setExpandedItems([]) // 목록 아코디언 모두 닫은 상태에서 페이지 전환
    }
  }, [currentpage, orderMode])

  // [조회 버튼, 목록수 변경 데이터 호출]
  useEffect(() => {
    setCurrentpage(1) // 페이지가 1인 상태에서 다시 목록이 업데이트 될 수 있게 설정
    getItems(currentsize, 1, send, orderMode)
  }, [send, currentsize])

  return (
    <DetailTableWrap>
      {data?.responseCode === TYPE.API_RESPONSE.NOT_DATA ||
      !data?.responseData ? (
        <Empty
          pointTxt={'조회된 주문현황'}
          txt={'이 없습니다.'}
          maxWidth={984}
        />
      ) : (
        <>
          <BtwBox>
            <AllOpenBtn type="button" onClick={toggleExpandAll}>
              {allExpanded ? '모두 닫기' : '모두 펼쳐보기'}
              <AllOpenImg
                src={IconDown}
                alt="화살표아이콘"
                isActive={allExpanded}
              />
            </AllOpenBtn>
            <DropDownWidth>
              <DropDown
                defaultOption={currentsize}
                options={sizeOption}
                setOption={setCurrentsize}
                value={currentsize}
                isPageUnit={true}
              />
            </DropDownWidth>
          </BtwBox>
          <TableUl>
            <Thead>
              <div></div>
              <div>번호</div>
              <div>주문번호</div>
              <div>주문시간</div>
              <div>주문유형</div>
              <div>상세유형</div>
              <div>주문내역</div>
              <div>주문상태</div>
              <div>배달상태</div>
              <div>결제금액</div>
            </Thead>
            {orderData?.map((item, i) => {
              const itemNumber = (currentpage - 1) * currentsize + i + 1
              const isItemExpanded = expandedItems.includes(item.orderNum)
              return (
                <OrderItem key={i}>
                  <button
                    className="orderItemBtn"
                    type="button"
                    onClick={() => toggleExpand(item.orderNum)}
                  >
                    <ArrowBox isActive={isItemExpanded}>
                      <img src={IconDown} alt="아래화살표" />
                    </ArrowBox>
                    <p>{itemNumber ?? ''}</p>
                    <p>{item?.orderNum ?? ''}</p>
                    <p>
                      {moment(item?.RegDT ?? '', 'YYYYMMDDHHmmss').format(
                        'YYYY. MM. DD.(ddd) A h:mm:ss',
                      )}
                    </p>
                    <p>{item?.orderType ?? '' === 'D' ? '배달' : '픽업'}</p>
                    <p>{getOrderDetail(item.orderDetail)}</p>
                    <p>{item?.goodsName ?? ''}</p>
                    <p>{item?.orderStatusName ?? ''}</p>
                    <p>
                      {item?.orderType === 'P'
                        ? !item?.deliveryStatusName
                          ? '픽업완료'
                          : '-'
                        : !item?.deliveryStatusName
                        ? '-'
                        : item?.deliveryStatusName}
                    </p>
                    <p>{getComma(item?.payMoney)}원</p>
                  </button>
                  {isItemExpanded && (
                    <DetailItem
                      orderList={item}
                      hiddenOrder={hiddenOrder}
                      isComplete={item?.orderCompleteYn === 'Y'}
                      isPickup={item?.orderType === 'P'}
                      setOrderHistoryItem={setOrderHistoryItem}
                    />
                  )}
                </OrderItem>
              )
            })}
          </TableUl>
          <Paging
            isTemp={false}
            page={currentpage}
            count={count} // totalCount
            setPage={(e) => {
              nextPage(e)
              setPageCount(pageCount + 1)
            }}
            marginTop={[40, 40]}
            tableSize={currentsize}
          />
        </>
      )}
    </DetailTableWrap>
  )
}

export default OrderDetail

const DetailTableWrap = styled.section`
  width: 100%;
  max-width: 984px;
  margin: 0 auto;
`

const TableUl = styled.ul`
  display: flex;
  flex-direction: column;
`
// 10개
const Thead = styled.li`
  padding: 15px 10px;
  display: grid;
  grid-template-columns: 0.2fr 0.3fr 1fr 1.2fr 0.5fr 1fr 1.2fr repeat(3, 0.7fr);
  justify-items: center;
  background-color: #f3f3f3;
  text-align: center;
  font-size: 14px;
  color: #a8a8a8;
  border-top: 1px solid #e5e5e5;
  border-bottom: 1px solid #e5e5e5;
`

const OrderItem = styled.li`
  .orderItemBtn {
    width: 100%;
    padding: 19px 10px;
    display: grid;
    grid-template-columns: 0.2fr 0.3fr 1fr 1.2fr 0.5fr 1fr 1.2fr repeat(
        3,
        0.7fr
      );
    align-items: center;
    justify-items: center;
    text-align: center;
    border-bottom: 1px solid #e5e5e5;

    p {
      max-width: 129px;
      min-height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      font-size: 16px;
      color: #000;
      line-height: 1.5;

      word-break: break-all;
      white-space: pre-wrap;
    }
  }
`
const ArrowBox = styled.p`
  transition: transform 0.5s ease;
  transform: ${(props) => (props.isActive ? `rotate(180deg)` : `rotate(0deg)`)};
`
const BtwBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 0px 0 28px;
  /* @media ${(props) => props.theme.mobile} {
    padding: 0 20px;
  } */
`
const DropDownWidth = styled.div`
  width: 162px;
`

const AllOpenBtn = styled.button`
  width: 114px;
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #000;
  padding: 0;
  text-align: left;
  /* img {
    margin-left: 5px;
    transition: transform 0.5s ease;
    transform: ${(props) =>
    props.isActive ? `rotate(180deg)` : `rotate(0deg)`};
  } */
`
const AllOpenImg = styled.img`
  margin-left: 5px;
  transition: transform 0.5s ease;
  transform: ${(props) => (props.isActive ? `rotate(180deg)` : `rotate(0deg)`)};
`
